import { Link } from 'found';

import PropTypes from 'prop-types';
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, injectIntl } from 'react-intl';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';

import {
  getTradePointTradeRuleTableLabel,
} from 'src/helpers/tradeHelpers';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
} from 'src/util/constants';
import username from 'src/util/decorators/username';
import { tradeRuleClauseConditions } from 'src/util/tradeRule';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';

import TradeRuleListTableItemClause from './TradeRuleListTableItemClause';

class TradeRuleListTableItem extends React.Component {
  static tradeRuleClauseConditions(intl, rule) {
    const conditions = tradeRuleClauseConditions(rule, intl);
    if (conditions.length === 0) {
      return { element: null, display: false };
    }

    return {
      element: (
        <li>{intl.formatList(conditions, { type: 'unit' })}</li>
      ),
      display: true,
    };
  }

  static counterPartyTrader(tradePointId, rule) {
    const direction = this.direction(tradePointId, rule);

    let trader;
    if (direction === TRADE_DIRECTION_BUY) {
      trader = rule.seller;
    } else if (direction === TRADE_DIRECTION_SELL) {
      trader = rule.buyer;
    } else {
      return null;
    }

    if (trader.user) {
      return trader.user;
    }
    if (trader.community) {
      return trader.community;
    }
    if (trader.residual) {
      return trader.residual;
    }

    return null;
  }

  static counterPartyTradePoint(tradePointId, rule) {
    const direction = this.direction(tradePointId, rule);

    let tradePoint;
    if (direction === TRADE_DIRECTION_BUY) {
      tradePoint = rule.seller.tradePoint;
    } else if (direction === TRADE_DIRECTION_SELL) {
      tradePoint = rule.buyer.tradePoint;
    } else {
      return null;
    }

    return getTradePointTradeRuleTableLabel(tradePoint);
  }

  static direction(tradePointId, rule) {
    const { buyer, seller } = rule;

    let buyerTradePointId = null;
    let sellerTradePointId = null;

    if (buyer.tradePoint && buyer.tradePoint.id) {
      buyerTradePointId = buyer.tradePoint.id;
    }
    if (seller.tradePoint && seller.tradePoint.id) {
      sellerTradePointId = seller.tradePoint.id;
    }

    if (buyerTradePointId && buyerTradePointId === tradePointId) {
      return TRADE_DIRECTION_BUY;
    }
    if (sellerTradePointId && sellerTradePointId === tradePointId) {
      return TRADE_DIRECTION_SELL;
    }
    return TRADE_DIRECTION_UNSPECIFIED;
  }

  render() {
    if (this.error) {
      return <div><FormattedMessage id="error.title" defaultMessage="Error!" /></div>;
    }
    if (!this.props) {
      return <Loading />;
    }

    const {
      propertyId, rule, tradePointId, intl,
    } = this.props;

    const {
      id, tradeType, state, start, finish,
    } = rule;
    const active = { start, finish };
    const clauses = (!rule.clauses || !rule.clauses.edges)
      ? [] : rule.clauses.edges.map((el) => (el.node)).sort((a, b) => (b.price - a.price));

    const clauseConditions = TradeRuleListTableItem.tradeRuleClauseConditions(intl, rule);

    // eslint-disable-next-line max-len -- conflict between max-len and function-paren-newline.
    const counterPartyTradePoint = TradeRuleListTableItem.counterPartyTradePoint(tradePointId, rule);
    const counterPartyTrader = TradeRuleListTableItem.counterPartyTrader(tradePointId, rule);
    const direction = TradeRuleListTableItem.direction(tradePointId, rule);

    return (
      <tr key={`trade-rule-${id}`}>
        <th>
          <Link to={`/properties/${propertyId}/trade-rules/${id}`}>
            <UUID uuid={id} />
          </Link>
        </th>
        <td>
          <BadgeTradeType type={tradeType} />
        </td>
        <td>
          <BadgeTradeRuleState state={state} />
        </td>
        <td>
          <BadgeTradeDirection direction={direction} />
        </td>
        <td>
          {counterPartyTrader && (
            <>
              <strong>{username(counterPartyTrader)}</strong>
              <br />
              <UUID uuid={counterPartyTrader.id} />
            </>
          )}
        </td>
        <td>
          {counterPartyTradePoint}
        </td>
        <td>
          <ul className="ps-2">
            {clauses && clauses.map((clause) => (
              <TradeRuleListTableItemClause
                clause={clause}
                key={clause.id}
                showConditions={false}
              />
            ))}
            {clauseConditions.display && clauseConditions.element}
          </ul>
        </td>
        <td data-testid="validity">
          {buildTimeRangeSummary(intl, active)}
        </td>
      </tr>
    );
  }
}

TradeRuleListTableItem.propTypes = {
  propertyId: PropTypes.string.isRequired,
  rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  tradePointId: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default injectIntl(createFragmentContainer(
  TradeRuleListTableItem,
  {
    rule: graphql`
      fragment TradeRuleListTableItem_rule on TradeRule {
        id
        buyer {
          user { id email givenName familyName }
          community { id title }
          residualId
          tradePoint {
            id
            type
            meter {
              id
              identifier
              title
              property {
                id
                title
              }
            }
          }
        }
        seller {
          user { id email givenName familyName }
          community { id title }
          residualId
          tradePoint {
            id
            type
            meter {
              id
              identifier
              title
              property {
                id
                title
              }
            }
          }
        }
        clauses {
          edges {
            node {
              id
              price
              ignoreDaylightSavings
              ignorePublicHolidays
              timezone
              ...TradeRuleListTableItemClause_clause
            }
          }
        }
        tradeType
        state
        start
        finish
        proposedAt
        proposedBy {id email givenName familyName }
        acceptedAt
        acceptedBy {id email givenName familyName }
        rejectedAt
        rejectedBy {id email givenName familyName }
        cancelledAt
        cancelledBy {id email givenName familyName }
        closedAt
        closedBy {id email givenName familyName }
      }
    `,
  },
));
